import { translate, translateRich, useLocale } from "../localization";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import BatteryChargingFullRoundedIcon from "@mui/icons-material/BatteryChargingFullRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AppButton from "../components/ui/AppButton";
import { usePageMetadata } from "../context/PageMetadataContext";
import "./SetupDownloadsPage.scss";

const sections = () => [["device-setup", translate("Install 10k Science")], ["organization", translate("Connect your organization")], ["first-class", translate("Before your first class")], ["help", translate("Troubleshooting & help")]];

const ExternalGuide = ({
  href,
  children
}) => {
  useLocale();
  return <AppButton kind="ghost" component="a" href={href} target="_blank" rel="noopener noreferrer" endIcon={<ArrowOutwardRoundedIcon />}>
    {children}
  </AppButton>;
};

const HeadsetPicture = () => {
  useLocale();
  return <svg viewBox="0 0 96 72" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 31v-5C20 12 76 12 76 26v5" />
    <path d="M23 28h50a9 9 0 0 1 9 9v15a9 9 0 0 1-9 9H60l-7-7H43l-7 7H23a9 9 0 0 1-9-9V37a9 9 0 0 1 9-9Z" />
    <path d="M28 41h40M37 47h22" />
  </svg>;
};

const connectionSteps = () => [{
  title: translate("Open your join code"),
  description: translate("On the dashboard, select Join Code on an experience. Keep an active code open."),
  picture: <ConfirmationNumberOutlinedIcon />
}, {
  title: translate("Connect the headset"),
  description: translate("Look behind you and choose Organization Setup, then Enter Join Code. Look at the join code on your computer screen to scan it."),
  picture: <HeadsetPicture />
}, {
  title: translate("Start exploring"),
  description: translate("Browse your organization\u2019s experiences or the default library."),
  picture: <AutoStoriesOutlinedIcon />
}];

const subtitleLanguages = () => [["en", "English"], ["es-419", "Español (Latinoamérica)"], ["ar", "العربية"], ["zh-Hans", "简体中文"], ["vi", "Tiếng Việt"], ["pt-BR", "Português (Brasil)"], ["tr", "Türkçe"], ["fr", "Français"], ["it", "Italiano"], ["de", "Deutsch"], ["ru", "Русский"], ["hi", "हिन्दी"]];

const classroomChecks = () => [{
  title: translate("Charge & connect"),
  description: translate("Charge the headsets and connect to Wi-Fi."),
  icon: <BatteryChargingFullRoundedIcon />
}, {
  title: translate("Check your organization"),
  description: translate("Make sure its experiences appear in the app."),
  icon: <SchoolOutlinedIcon />
}, {
  title: translate("Try an experience"),
  description: translate("Open your lesson and let it finish loading."),
  icon: <PlayCircleOutlineRoundedIcon />
}, {
  title: translate("Check fit & sound"),
  description: translate("Adjust the headset, audio, and language."),
  icon: <TuneRoundedIcon />
}];

const SetupDownloadsPage = () => {
  const locale = useLocale();
  const videoRef = useRef(null);
  const [videoStarted, setVideoStarted] = useState(false);
  const { setTitle, setShowTitleInHeader } = usePageMetadata();
  const { hash } = useLocation();

  useEffect(() => {
    setTitle(translate("VR setup"));
    setShowTitleInHeader(true);
  }, [setTitle, setShowTitleInHeader, locale]);

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [hash]);

  return (
    <main className="setupPage">
      <div className="setupPage__layout">
        <div className="setupPage__content">
          <section id="device-setup" className="setupPage__section" aria-labelledby="device-setup-title">
            <p className="setupPage__eyebrow">{translate("01 / INSTALL")}</p>
            <h2 id="device-setup-title">{translate("Install 10k Science")}</h2>
            <p>{translate("Choose how you manage your Meta Quest headsets.")}</p>
            <p className="setupPage__note">{translateRich("Already have the app? {value0}.", {
  value0: <a href="#organization">{translate("Connect your organization")}</a>
})}</p>
            <div className="setupPage__options app-row-list">
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">{translateRich("Individual headset{value0}", {
  value0: <span>{translate("Get the app from the Meta Horizon Store")}</span>
})}</summary>
                <div className="setupPage__detail">
                  <p>{translate("Install 10k Science from the store, then open it on your headset.")}</p>
                  <AppButton kind="primary" component="a" href="https://www.meta.com/experiences/10k-science/5616986801647861/" target="_blank" rel="noopener noreferrer" endIcon={<ArrowOutwardRoundedIcon />}>{translate("Get 10k Science")}</AppButton>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">{translateRich("Horizon managed services (HMS){value0}", {
  value0: <span>{translate("Find the app in Discover Apps")}</span>
})}</summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>{translate("In Meta\u2019s Device Manager, open Apps & Content \u2192 Discover Apps.")}</li>
                    <li>{translate("Find 10k Science and select Add to your apps.")}</li>
                    <li>{translate("Assign the app to your headsets through their device or shared-mode settings.")}</li>
                  </ol>
                  <ExternalGuide href="https://devicemanager.meta.com/apps/discover_apps">{translate("Open Discover Apps")}</ExternalGuide>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">{translateRich("ManageXR{value0}", {
  value0: <span>{translate("Discover XR / Marketplace")}</span>
})}</summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>{translate("Open Marketplace (Discover XR) and find 10k Science under Instant Apps.")}</li>
                    <li>{translate("Add it to your VR Content Library, then to a configuration.")}</li>
                    <li>{translate("Apply that configuration to your headsets.")}</li>
                  </ol>
                  <div className="setupPage__actions">
                    <ExternalGuide href="https://console.managexr.com/">{translate("Open ManageXR")}</ExternalGuide>
                    <ExternalGuide href="https://help.managexr.com/en/articles/7936014-managexr-marketplace">{translate("Marketplace guide")}</ExternalGuide>
                  </div>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">{translateRich("ArborXR{value0}", {
  value0: <span>{translate("Find the app in Explore XR")}</span>
})}</summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>{translate("In Explore XR, find 10k Science and select Add+.")}</li>
                    <li>{translate("Open Content Library \u2192 Demo Apps and select 10k Science.")}</li>
                    <li>{translate("Choose Add to Group or Deploy to Ungrouped Devices, then select your headsets.")}</li>
                  </ol>
                  <div className="setupPage__actions">
                    <ExternalGuide href="https://arborxr.com/demo-apps/10k-science">{translate("10k Science on ArborXR")}</ExternalGuide>
                    <ExternalGuide href="https://api.xrdm.app/explore-xr">{translate("Open Explore XR")}</ExternalGuide>
                  </div>
                </div>
              </details>
            </div>
          </section>

          <section id="organization" className="setupPage__section" aria-labelledby="organization-title">
            <p className="setupPage__eyebrow">{translate("02 / CONNECT")}</p>
            <h2 id="organization-title">{translate("Connect your organization")}</h2>
            <p>{translate("One-time setup on each headset.")}</p>
            <ol className="setupPage__steps" aria-label={translate("Connect in three steps")}>
              {connectionSteps().map(({ title, description, picture }, index) => (
                <li key={title}>
                  <span className="setupPage__stepNumber" aria-hidden="true">{index + 1}</span>
                  <div className="setupPage__picture" aria-hidden="true">{picture}</div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </li>
              ))}
            </ol>
            <p className="setupPage__note">{translate("Your headset remembers the organization. You\u2019ll only need to reconnect if the app\u2019s saved data is cleared.")}</p>
            <figure className="setupPage__walkthrough">
              <div className="setupPage__videoPlayer">
              <video id="organization-setup-video" ref={videoRef} controls playsInline preload="none" poster="/media/vr-setup/join-code-poster.jpg" aria-label={translate("Connect your headset to your organization")} onPlay={() => setVideoStarted(true)} onEnded={() => setVideoStarted(false)}>
                <source src="/media/vr-setup/join-code.webm" type="video/webm" />
                <source src="/media/vr-setup/join-code.mp4" type="video/mp4" />
                {subtitleLanguages().map(([language, label]) => (
                  <track key={language} kind="subtitles" src={`/media/vr-setup/join-code.${language}.vtt`} srcLang={language} label={label} default={language === "en"} />
                ))}
                <a href="/media/vr-setup/join-code.mp4">{translate("Watch the setup video")}</a>
              </video>
              {!videoStarted && (
                <div className="setupPage__videoPlayOverlay">
                  <AppButton kind="primary" startIcon={<PlayCircleOutlineRoundedIcon />} aria-controls="organization-setup-video" onClick={() => videoRef.current?.play()?.catch(() => videoRef.current?.focus())}>
                    {translate("Watch the setup video")}
                  </AppButton>
                </div>
              )}
              </div>
              <figcaption>{translate("Choose subtitles in 12 languages using the video\u2019s captions menu.")}</figcaption>
            </figure>
          </section>

          <section id="first-class" className="setupPage__section" aria-labelledby="first-class-title">
            <p className="setupPage__eyebrow">{translate("03 / GET READY")}</p>
            <h2 id="first-class-title">{translate("Before your first class")}</h2>
            <ul className="setupPage__checklist">
              {classroomChecks().map(({ title, description, icon }) => (
                <li key={title}>
                  <span className="setupPage__checkIcon" aria-hidden="true">{icon}</span>
                  <div><h3>{title}</h3><p>{description}</p></div>
                </li>
              ))}
            </ul>
            <AppButton kind="secondary" component={Link} to="/">{translate("Browse classroom experiences")}</AppButton>
          </section>

          <section id="help" className="setupPage__section" aria-labelledby="help-title">
            <p className="setupPage__eyebrow">{translate("NEED A HAND?")}</p>
            <h2 id="help-title">{translate("Troubleshooting & help")}</h2>
            <div className="setupPage__options app-row-list">
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">{translate("App missing?")}</summary>
                <div className="setupPage__detail">
                  <ul>
                    <li>{translate("Managed headset: check that the app is assigned and installation has finished.")}</li>
                    <li>{translate("Individual headset: check that the store download has finished.")}</li>
                  </ul>
                  <p>{translate("Still missing? Ask your headset administrator to check.")}</p>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">{translate("Join code not working?")}</summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>{translate("Check Wi-Fi and enter the code again.")}</li>
                    <li>{translate("Ask the person who shared it to check that it\u2019s still valid and belongs to your organization.")}</li>
                  </ol>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">{translate("Updating the app?")}</summary>
                <div className="setupPage__detail">
                  <ul>
                    <li>{translate("Managed headset: ask your administrator to deploy the latest version.")}</li>
                    <li>{translate("Individual headset: update 10k Science through the store.")}</li>
                  </ul>
                  <p>{translate("Keep the app\u2019s saved data so it remembers your organization.")}</p>
                </div>
              </details>
            </div>
            <p className="setupPage__note">{translate("Still stuck? Tell us what happened and which headset you\u2019re using.")}</p>
            <AppButton kind="secondary" component={Link} to="/contact">{translate("Contact 10k Science")}</AppButton>
          </section>
        </div>
        <aside className="setupPage__aside">
          <nav aria-label={translate("On this page")}>
            <p className="setupPage__eyebrow">{translate("ON THIS PAGE")}</p>
            {sections().map(([id, label]) => <AppButton key={id} kind="ghost" component="a" href={`#${id}`}>{label}</AppButton>)}
          </nav>
        </aside>
      </div>
    </main>
  );
};

export default SetupDownloadsPage;
